import BotCheckbox from '@common-components/bt-chatbot/bot-components/bot-checkbox';
import BotInputField from '@common-components/bt-chatbot/bot-components/bot-input-field';
import BotMessageEditor from '@common-components/bt-chatbot/bot-components/bot-message-editor';
import BTbotbutton from '@common-components/bt-chatbot/bot-components/bt-bot-button';
import RowStack from '@common-components/common/row-stack/RowStack';
import { setCurrentBot } from '@common/reducers/ChatbotReducer';
import { getCurrentBotSelector, getNodeDataByNodeId } from '@common-reducers/ChatbotSelectors';
import { changeNodeDataThunk } from '@common-reducers/ChatbotThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Asset } from '@common/types/interface';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './llm-dialog.module.scss';
import { namespace } from '@common/config/constants';

interface Props {
    onClose: () => void;
    id?: string;
}

function AddLLMDialog({ onClose, id }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();

    // Get node data if editing
    const data = useAppSelector(state => getNodeDataByNodeId(state, id ?? ''));
    const currentBot = useAppSelector(state => getCurrentBotSelector(state));

    type LLMNodeData = {
        text: string;
        asset: Asset | null | undefined;
        includeSessionHistory: boolean;
        sendResponseToUser: boolean;
        [key: string]: unknown;
    };

    // Local state for the form
    const [localNodeData, setLocalNodeData] = useState<LLMNodeData>({
        ...data,
        text: data?.text || '',
        asset: data?.asset || null,
        includeSessionHistory: data?.includeSessionHistory || false,
        sendResponseToUser: data?.sendResponseToUser ?? false // Default to true for backward compatibility
    });
    const [localCurrentBot, setLocalCurrentBot] = useState<{ botSettings?: { variables?: Record<string, string> } } | undefined>(currentBot);

    // Handle message content change
    const onChange = useCallback((value: string | { message: string; asset?: Asset }) => {
        setLocalNodeData(prev => ({
            ...prev,
            text: typeof value === 'object' ? value.message : value,
            asset: typeof value === 'object' ? value.asset : undefined
        }));
    }, []);

    // Handle session history checkbox change
    const toggleIncludeSessionHistory = useCallback(() => {
        setLocalNodeData(prev => ({
            ...prev,
            includeSessionHistory: !prev.includeSessionHistory
        }));
    }, []);

    // Handle send response checkbox change
    const toggleSendResponseToUser = useCallback(() => {
        setLocalNodeData(prev => ({
            ...prev,
            sendResponseToUser: !prev.sendResponseToUser
        }));
    }, []);

    // Handle variable name input change
    const onChangeVariableName = useCallback((value: string) => {
        setLocalCurrentBot(prev => ({
            ...(prev ?? {}),
            botSettings: {
                ...(prev?.botSettings ?? {}),
                variables: {
                    ...(prev?.botSettings?.variables ?? {}),
                    [id ?? '']: value
                }
            }
        }));
    }, [id]);

    // Save LLM data
    const saveLLMData = useCallback(async () => {
        if (id) {
            await dispatch(changeNodeDataThunk({
                id,
                dataToPatch: localNodeData
            }));
            await dispatch(setCurrentBot(localCurrentBot));
        }
        onClose();
    }, [dispatch, id, localNodeData, localCurrentBot, onClose]);

    // Message editor component
    const messageEditorDialogMemo = useMemo(() => (
        <>
            <div className={classes.llm_dialog_title}>{t('chatbotNodes.llmPrompt')}</div>
            <BotMessageEditor
                _message={{
                    message: localNodeData.text || '',
                    asset: localNodeData.asset ?? undefined
                }}
                onChange={onChange}
                type='LLM'
            />
        </>
    ), [localNodeData.text, localNodeData.asset, onChange]);

    return (
        <div className={classes.llm_dialog_wrapper}>
            <div className={classes.llm_dialog_content}>
                {messageEditorDialogMemo}

                <div className={classes.divider} />

                <div className={classes.settings_section}>
                    <BotCheckbox 
                        id="includeSessionHistory"
                        checked={localNodeData.includeSessionHistory}
                        onChange={toggleIncludeSessionHistory}
                        title={t('chatbotNodes.includeConversationHistory')}
                        description={t('chatbotNodes.includeConversationHistoryDescription')}
                    />
                    
                    <BotCheckbox 
                        id="sendResponseToUser"
                        checked={localNodeData.sendResponseToUser}
                        onChange={toggleSendResponseToUser}
                        title={t('chatbotNodes.sendResponseToUser')}
                        description={t('chatbotNodes.sendResponseToUserDescription')}
                    />

                    <div className={classes.variable_section}>
                        <div className={classes.variable_label}>{t('chatbotNodes.saveLLMResponseVariable')}</div>
                        <BotInputField
                            value={localCurrentBot?.botSettings?.variables?.[id ?? ''] ?? ''}
                            onChange={(newValue) => onChangeVariableName(newValue)}
                            placeholder={t('chatbotNodes.variableNamePlaceholder')}
                        />
                        <div className={classes.variable_hint}>
                            {t('chatbotNodes.variableHint', { variable: localCurrentBot?.botSettings?.variables?.[id ?? ''] || 'variable_name' })}
                        </div>
                    </div>
                </div>

                <div className={classes.button_container}>
                    <RowStack sx={{ justifyContent: 'flex-end' }}>
                        <BTbotbutton styleType='empty' onClick={onClose} sx={{ marginLeft: "13px" }}>{t('common.cancel')}</BTbotbutton>
                        <BTbotbutton onClick={saveLLMData} sx={{ marginLeft: "13px" }}>{t('common.save')}</BTbotbutton>
                    </RowStack>
                </div>
            </div>
        </div>
    );
}

export default memo(AddLLMDialog);
